import {React, useState, useEffect} from 'react';
import getBackend from 'backend/backend';
import { Paths } from 'paths';
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router-dom';
import { Alert, Container, Card, Row, Col, Badge, Form, Button, ProgressBar } from 'react-bootstrap';
import SidebarPage from 'components/sidebar_page';
import { showConfirmation } from 'components/misc';

const DataIndex = () => {
    const [t] = useTranslation('common');

    const dataIndexId = useParams().dataIndexId;
    const [error, setError] = useState(null);
    const [updateSeqNo, setUpdateSeqNo] = useState(0);
    const [dataIndex, setDataIndex] = useState(null);
    const [files, setFiles] = useState([]);
    const [progress, setProgress] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setUpdateSeqNo(prev => prev + 1);
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        let canceled = false;

        const getDataIndex = async () => {
            const result = await getBackend().getDataIndex(dataIndexId);
            if (canceled)
                return;

            if (result.error === null) {
                setDataIndex(result.response.data_index);
            } else {
                setError(result.error);
                window.location.href = Paths.NotFound.path;
            }
        };

        getDataIndex();

        return async () => {
            canceled = true;
        }
    }, [dataIndexId, updateSeqNo]);

    const handleFileChange = (e) => {
        setFiles(e.target.files);
    };

    const handleUpload = async (e) => {
        e.preventDefault();

        if (!files || files.length === 0) {
            setError("No files selected");
            return;
        }

        setError(null);
        setShowProgress(true);
        setProgress(0); // Start at 0

        try {
            const totalFiles = files.length;
            let completedFiles = 0;

            // Process files sequentially
            for (const file of files) {
                try {
                    const fileContent = await readFileAsDataURL(file);
                    const result = await getBackend().updateDataIndex(dataIndexId, {
                        command: "upload_file",
                        file_name: file.name,
                        file_content: fileContent
                    });

                    if (result.error !== null) {
                        throw new Error(result.error);
                    }

                    completedFiles++;
                    // Update progress based on completed files
                    setProgress((completedFiles / totalFiles) * 100);
                } catch (err) {
                    setError(`Error uploading ${file.name}: ${err.message}`);
                    break;
                }
            }

            // Reset form and update UI
            setFiles([]);
            setUpdateSeqNo(prev => prev + 1);
            document.querySelectorAll('input[type="file"]').forEach(input => {
                input.value = '';
            });
        } catch (err) {
            setError(`Upload failed: ${err.message}`);
        } finally {
            // Hide progress after short delay to show completion
            setTimeout(() => {
                setShowProgress(false);
                setProgress(0);
            }, 1000);
        }
    };

    // Helper function to read file
    const readFileAsDataURL = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(new Error('File read failed'));
            reader.readAsDataURL(file);
        });
    };

    const handleStartIndexing = async () => {
        setError(null);

        const result = await getBackend().updateDataIndex(dataIndexId, {
            command: "start_indexing"
        });

        if (result.error !== null) {
            setError(result.error);
            return;
        }

        setUpdateSeqNo(prev => prev + 1);
    }

    const deleteDataIndex = async (id) => {
        let result = await showConfirmation("Delete data index", "Are you really going to delete the data index? All data will be lost.");
        if (result.isConfirmed) {
            const result = await getBackend().deleteDataIndex(id);
            if (result.error !== null) {
                setError(result.error);
            } else {
                window.location.href = Paths.DataIndexes.path;
            }
        }
    }

    return (
        <SidebarPage title={t('Data Index')} description={t('Data Index')}>
            {error && <Alert variant="danger">{error}</Alert>}
            {dataIndex &&
            <Container className="my-5">
                <h2 className="mb-4">{t('Data Index')}</h2>
                <Card className="mb-4 shadow-sm">
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <h5 className="text-muted d-inline">ID: </h5>
                                    <div className="font-monospace d-inline">{dataIndex.id}</div>
                                </div>
                                <div className="mb-3">
                                    <h5 className="text-muted d-inline">File system path: </h5>
                                    <div className="d-inline">{dataIndex.file_system_path}</div>
                                </div>
                                <div className="mb-3">
                                    <h5 className="text-muted d-inline">State: </h5>
                                    <Badge bg={dataIndex.state === "indexed" ? "success" : "warning"} className="px-3 py-2">
                                        {dataIndex.state}
                                    </Badge>
                                </div>
                                {dataIndex.error &&
                                <div className="mb-3">
                                    <h5 className="text-muted d-inline">Error: </h5>
                                    <div className="d-inline" style={{color: "red"}}>{dataIndex.error}</div>
                                </div>
                                }
                                <div className="mb-3">
                                    <h5 className="text-muted d-inline">File count: </h5>
                                    <div className="d-inline">{dataIndex.file_count}</div>
                                </div>
                                <div className="mb-3">
                                    <h5 className="text-muted d-inline">Disk Usage: </h5>
                                    <div className="d-inline">{dataIndex.disk_usage}</div>
                                </div>

                                {dataIndex.state === "created" &&
                                <div className="mb-3">
                                    <Form onSubmit={handleUpload}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('Select files to upload')}</Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept=".pdf"
                                                multiple
                                                onChange={handleFileChange}
                                                className="form-control-file"
                                            />
                                            <Form.Text className="text-muted">
                                                {t('You can select multiple files by holding Ctrl/Cmd while selecting')}
                                            </Form.Text>
                                        </Form.Group>
                                        <Button 
                                            type="submit" 
                                            className="custom-button" 
                                            variant="light"
                                            disabled={showProgress}
                                        >
                                            {showProgress ? t('Uploading...') : t('Upload Files')}
                                        </Button>
                                    </Form>
                                    {showProgress && (
                                        <div className="mt-3">
                                            <ProgressBar 
                                                animated 
                                                now={progress} 
                                                label={`${Math.round(progress)}%`}
                                                variant="primary"
                                            />
                                        </div>
                                    )}
                                </div>
                                }

                                {dataIndex.state === "created" && dataIndex.file_count > 0 &&
                                <div className="mb-3">
                                    <Button className="custom-button" variant="light" onClick={handleStartIndexing}>{t('Start Data Indexing')}</Button>
                                </div>
                                }
                                <Button 
                                        className="my-2"
                                        variant="danger" 
                                        onClick={() =>
                                            deleteDataIndex(dataIndex.id)
                                        }
                                    >
                                        {t('Delete Data Index')}
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </Container>
            }
        </SidebarPage>
    );
};

export default DataIndex;
